// 스킬 그림 DOM 조각(2026-10-27 회차): 스킬 도감(26칸 로드맵, 전직 갈래 문장)·각성 그림 한 칸. 경로는 skillIcons.ts(순수).
// 그림이 없으면 칸을 지우지 않고 짧은 글자(스킬 이름 앞 두 글자)를 보인다(화면 모양이 깨지지 않게). 창을 다시 그릴 때만 부른다.
import { HEROES_90D } from '../shared/content/heroes';
import type { HeroId } from '../shared/content/types';
import { assetUrl } from '../pwa/assetMap';
import { FONT, FONT_NUM, el } from './dom';
import { jobEmblemPath, skillRoad } from './skillIcons';

const STYLE_ID = 'dh-skill-road-style';
const CSS =
  '.dh-sr{margin-top:4px}' +
  '.dh-sr>summary{list-style:none}.dh-sr>summary::-webkit-details-marker{display:none}' +
  '.dh-sr-jobs{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin:2px 0 6px}' +
  '.dh-sr-job{display:flex;align-items:center;gap:6px;min-width:0;padding:4px 6px;border:1px solid #4b627c;border-radius:8px;background:#0b1c30}' +
  '.dh-sr-job img{width:34px;height:34px;flex:0 0 34px;object-fit:cover;border-radius:6px;border:1px solid #a99159}' +
  '.dh-sr-job b{min-width:0;font:800 12px ' + FONT + ';color:#f5df9f;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
  '.dh-sr-job i{flex:0 0 auto;font:900 11px ' + FONT_NUM + ';font-style:normal;color:#0b1220;background:#e8cd86;border-radius:4px;padding:0 4px}' +
  '.dh-sr-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(50px,1fr));gap:5px}' +
  '.dh-sr-cell{position:relative;aspect-ratio:1;min-width:0;border:1px solid #8091a6;border-radius:6px;background:#071321;overflow:hidden}' +
  '.dh-sr-cell img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}' +
  '.dh-sr-cell .tx{position:absolute;inset:0;display:none;place-items:center;font:800 11px ' + FONT + ';color:#c4cfdf;text-align:center;line-height:1.1;padding:2px}' +
  '.dh-sr-cell.noimg .tx{display:grid}' +
  '.dh-sr-cell.locked img{filter:grayscale(.85) brightness(.45)}.dh-sr-cell.locked{border-color:#37495d}' +
  '.dh-sr-cell .lv{position:absolute;left:1px;bottom:1px;padding:0 3px;border-radius:3px;background:#071321df;font:800 10px ' + FONT_NUM + ';font-style:normal;color:#fff}' +
  '.dh-sr-cell .jb{position:absolute;right:1px;top:1px;padding:0 3px;border-radius:3px;background:#e8cd86;color:#0b1220;font:900 10px ' + FONT_NUM + '}' +
  '.dh-sr-cell.k-ultimate{border-color:#ff8a5a}.dh-sr-cell.k-awaken{border-color:#f2c14e;box-shadow:inset 0 0 0 1px #f2c14e88}' +
  '.dh-sr-cell.k-job,.dh-sr-cell.k-awaken2{border-color:#e8cd86}' +
  // 잠긴 칸: 자물쇠(필요 레벨은 왼쪽 아래 숫자). 다음에 열릴 칸은 금빛 점선 테두리(2026-10-27 「레벨 모자란 칸은 자물쇠 + 필요 레벨」)
  '.dh-sr-cell.locked::after{content:"";position:absolute;left:50%;top:38%;width:16px;height:16px;margin:-8px 0 0 -8px;opacity:.9;pointer-events:none;' +
  'background:no-repeat center/contain url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27%3E%3Cpath fill=%27%23cfd6ee%27 d=%27M7 10V7a5 5 0 0 1 10 0v3h1a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V11a1 1 0 0 1 1-1zm2 0h6V7a3 3 0 0 0-6 0z%27/%3E%3C/svg%3E")}' +
  '.dh-sr-cell.next{border:1.5px dashed #f2c14e}.dh-sr-cell.next .lv{background:#f2c14e;color:#1a1204}';

function ensureStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent = CSS;
  document.head.appendChild(st);
}

/** 정사각 스킬 그림 한 장. 못 읽으면 parent에 noimg를 붙여 글자 대체를 보인다 */
export function skillTileImg(parent: HTMLElement, path: string, alt = ''): HTMLImageElement {
  const im = document.createElement('img');
  im.alt = alt; im.draggable = false; im.decoding = 'async';
  im.dataset.icon = path;
  im.addEventListener('error', () => { im.style.display = 'none'; parent.classList.add('noimg'); });
  im.src = assetUrl(path);
  parent.appendChild(im);
  return im;
}

/** 스킬 도감 열림 상태(창을 다시 그려도 유지) */
export interface SkillRoadState { open: boolean }

/**
 * 스킬 도감: 그 영웅의 아이콘 26칸(레벨 순, 배운 것 밝게 · 아직은 어둡게 + 해금 레벨), 위에 전직 갈래 문장 둘.
 * 갈래 전용 칸은 오른쪽 위에 A/B
 */
export function drawSkillRoad(parent: HTMLElement, hero: HeroId, level: number, state: SkillRoadState, job: { readonly tier: number; readonly branch: number } | null = null): HTMLDetailsElement | null {
  const h = HEROES_90D.find((x) => x.id === hero);
  if (!h) return null;
  ensureStyle();
  const cells = skillRoad(hero, level, job);
  const box = el('details', '', parent) as HTMLDetailsElement;
  box.className = 'dh-sr dh-sd-growth';
  box.dataset.testid = 'skill-road';
  box.open = state.open;
  box.addEventListener('toggle', () => { state.open = box.open; });
  el('summary', '', box, '스킬 도감 · ' + cells.filter((c) => c.learned).length + ' / ' + cells.length).className = 'dh-sd-more';
  const jobs = el('div', '', box);
  jobs.className = 'dh-sr-jobs';
  for (const j of [0, 1] as const) {
    const jb = el('div', '', jobs);
    jb.className = 'dh-sr-job';
    jb.dataset.testid = 'skill-road-job';
    skillTileImg(jb, jobEmblemPath(hero, j));
    el('i', '', jb, j === 0 ? 'A' : 'B');
    el('b', '', jb, h.jobs[j]);
  }
  const grid = el('div', '', box);
  grid.className = 'dh-sr-grid';
  // 다음에 열릴 칸(잠긴 칸 중 가장 낮은 레벨)
  let next = -1;
  for (let i = 0; i < cells.length; i++) if (!cells[i].learned && cells[i].kind !== 'awaken' && (next < 0 || cells[i].level < cells[next].level)) next = i;
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    const cell = el('span', '', grid);
    cell.className = 'dh-sr-cell k-' + c.kind + (c.learned ? '' : ' locked') + (i === next ? ' next' : '');
    cell.dataset.testid = 'skill-road-cell';
    cell.dataset.skill = c.id;
    const label = (c.kind === 'awaken' ? '각성 · ' : 'Lv' + c.level + ' · ') + c.name + (c.job !== null ? ' · ' + h.jobs[c.job] : '') + (c.learned ? '' : ' (잠김)');
    cell.title = label;
    cell.setAttribute('aria-label', label);
    cell.setAttribute('role', 'img');
    skillTileImg(cell, c.icon);
    el('span', '', cell, c.name.slice(0, 4)).className = 'tx';
    if (c.kind !== 'awaken') el('i', '', cell, String(c.level)).className = 'lv';
    if (c.job !== null) el('b', '', cell, c.job === 0 ? 'A' : 'B').className = 'jb';
  }
  return box;
}
